/**
 * inference/client.js — InferenceClient interface + Mock + Http implementations.
 *
 * The frontend depends on the InferenceClient contract. MockInferenceClient
 * reads fixtures, and HttpInferenceClient calls the backend when ?mock=0.
 *
 * THE FRONTEND NEVER BRANCHES ON WHICH CLIENT IS ACTIVE. It only reads
 * the contract. The diff between mock and http modes should be zero
 * lines in tool/ and harness/.
 *
 * The scope safeguard requires inference targets to be participant-authored.
 * Draft targets are synthetic participant messages built from current text.
 */

import { CHAT_DATA, MIRROR_DATA } from '../fixtures/data.js';
import { D2L1_DRAFT, AXIS_EVIDENCE } from '../fixtures/data-ext.js';
import { appConfig as defaultAppConfig } from '../app-config.js';
import { resolveClientMode } from './runtime.js';
import {
  CONTRACT_VERSION,
  CATEGORIES,
  RISK_AXES,
  ScopeViolationError,
  assertDraftRequest,
  assertDraftSpan,
  assertMessage,
  assertTranscript,
  assertMirrorAggregate,
  assertMeterResponse,
  assertMessageFindingsResponse,
  assertChatResponse,
} from './types.js';

const isDev = (() => {
  try {
    return Boolean(import.meta.env?.DEV);
  } catch {
    return false;
  }
})();

export const MOCK_LATENCY_MS = 0;

// Auto-Redact replacement fallback. When a DraftSpan from the backend
// (or a fixture) does not carry a `replacement` field, this string is
// substituted in the redact UI. The trailing ellipsis reads as in-progress
// masking rather than a finished state.
export const REDACTION_FALLBACK = 'redacting...';

export class LimitReachedError extends Error {
  constructor(limit = null) {
    super('Session limit reached');
    this.name = 'LimitReachedError';
    this.code = 'limit_reached';
    this.limit = limit === 'tokens' || limit === 'time' ? limit : null;
  }
}

const TRAIT_TO_CATEGORY = {
  Location: 'Sensitive',
  'Workplace area': 'Sensitive',
  'Home-to-work distance': 'Sensitive',
  'Place of Birth': 'Sensitive',
  'Field of study': 'Sensitive',
  Education: 'Sensitive',
  'Income bracket': 'Inferred',
  'Age range': 'Inferred',
  'Cultural background': 'Inferred',
  'Employment type': 'Inferred',
  Household: 'Inferred',
};

function categorize(rawInf) {
  // Prefer fixture-provided category; trait table is a backstop only.
  if (CATEGORIES.includes(rawInf.category)) return rawInf.category;
  return TRAIT_TO_CATEGORY[rawInf.trait] || 'Inferred';
}

/**
 * The inference target MUST be authored by 'me'. This client-layer assertion
 * protects the scope safeguard before any backend request is made.
 *
 * Throws ScopeViolationError so callers can distinguish the safeguard from
 * generic Error / network failures.
 *
 * @param {{ role?: string } | null | undefined} target
 */
export function assertOwnAuthorshipScope(target) {
  if (!target || target.role !== 'me') {
    throw new ScopeViolationError(
      "§6.5 scope safeguard: inference target must be authored by 'me'. " +
        'The Privacy Meter is not a profiler for arbitrary users.'
    );
  }
}

/**
 * Focused-analysis targets. This is the client-side twin of the server gate
 * (backend/routes/handlers.js + backend/scope-guard.js assertTranscriptScope):
 * targetMessageIds must be a non-empty unique string array, every id must
 * reference a transcript message, and every referenced message must be
 * authored by 'me'. Both clients call this so the safeguard holds even when
 * requests never reach the backend (defense in depth).
 *
 * @param {Array<{ id: string, role: string }>} messages
 * @param {unknown} targetMessageIds
 */
export function assertTargetScope(messages, targetMessageIds) {
  if (
    !Array.isArray(targetMessageIds) ||
    targetMessageIds.length === 0 ||
    targetMessageIds.some((id) => typeof id !== 'string') ||
    new Set(targetMessageIds).size !== targetMessageIds.length
  ) {
    throw new TypeError('[contract] targetMessageIds must be a non-empty unique string array');
  }
  const byId = new Map(messages.map((message) => [message.id, message]));
  for (const id of targetMessageIds) {
    const target = byId.get(id);
    if (!target)
      throw new TypeError('[contract] targetMessageIds must reference transcript messages');
    assertOwnAuthorshipScope(target);
  }
}

// Validate every history item's shape. The scope safeguard checks the
// inference target; this checks the surrounding context so a malformed
// history entry cannot smuggle past the contract layer.
function assertHistory(history) {
  if (!Array.isArray(history)) {
    throw new TypeError('[contract] history must be an array');
  }
  history.forEach(assertMessage);
}

function delay(ms) {
  return ms > 0 ? new Promise((r) => setTimeout(r, ms)) : Promise.resolve();
}

/**
 * Coerce a chat reply into the 3.1.0 { messages: ChatMessage[] } shape.
 *
 * Accepts either the new array shape or the legacy { text: string } single
 * reply (still emitted by older backends and by test mocks) and normalizes to
 * a non-empty messages array. Empty/blank messages are dropped; if nothing
 * survives, returns null so the caller can no-op rather than append a blank
 * bubble. Per-message thinkingMs/typingMs pacing overrides are preserved when
 * present so the backend can steer pacing; otherwise the hook computes them.
 *
 * @param {unknown} response
 * @returns {{ messages: Array<{ text: string, thinkingMs?: number, typingMs?: number }> } | null}
 */
export function normalizeChatResponse(response) {
  if (response === null || typeof response !== 'object') return null;
  const raw = Array.isArray(response.messages)
    ? response.messages
    : typeof response.text === 'string'
      ? [{ text: response.text }]
      : [];
  const messages = raw
    .map((m) => (typeof m === 'string' ? { text: m } : m))
    .filter((m) => m && typeof m.text === 'string' && m.text.trim().length > 0)
    .map((m) => {
      const out = { text: m.text };
      if (typeof m.thinkingMs === 'number' && Number.isFinite(m.thinkingMs) && m.thinkingMs >= 0)
        out.thinkingMs = m.thinkingMs;
      if (typeof m.typingMs === 'number' && Number.isFinite(m.typingMs) && m.typingMs >= 0)
        out.typingMs = m.typingMs;
      return out;
    });
  return messages.length ? { messages } : null;
}

function adaptInference(rawInf, srcText, idPrefix) {
  const start = 0;
  const end = srcText.length;
  return {
    id: `${idPrefix}-${rawInf.trait.replace(/\s+/g, '-').toLowerCase()}`,
    trait: rawInf.trait,
    category: categorize(rawInf),
    severity: typeof rawInf.confidence === 'number' ? rawInf.confidence : 0.5,
    confidence: typeof rawInf.confidence === 'number' ? rawInf.confidence : 0.5,
    reasoning: rawInf.why || '',
    spans: [{ start, end }],
  };
}

function buildMeterUpdate() {
  return CHAT_DATA.meter.map((row) => ({ ...row }));
}

function buildMirrorAggregate() {
  return {
    sessionLength: MIRROR_DATA.sessionLength,
    categories: MIRROR_DATA.categories.map((c) => ({
      key: c.key,
      confidence: c.confidence,
      summary: c.summary,
      evidence: c.evidence.map((e) => ({ snippet: e.snippet, msgId: String(e.msgId) })),
    })),
  };
}

function adaptAxisEvidence(axis) {
  const raw = AXIS_EVIDENCE[axis];
  if (!raw) return null;
  return {
    axis,
    explanation: raw.explainer,
    contributing: raw.items.map((it) => ({
      msgId: String(it.msgId),
      snippet: it.snippet,
      trait: it.trait,
      confidence: it.confidence,
    })),
  };
}

function buildDraftSpans(draftText) {
  const concat = D2L1_DRAFT.spans.map((s) => s.t).join('');
  if (concat !== draftText) return null;

  const out = [];
  let cursor = 0;
  for (const span of D2L1_DRAFT.spans) {
    const length = span.t.length;
    if (span.risk && span.inference) {
      out.push({
        start: cursor,
        end: cursor + length,
        severity: span.risk / 3,
        confidence: span.inference.confidence,
        inferredTrait: span.inference.trait,
        justification: span.inference.why,
        // ?? not || so an explicit '' from the backend (meaning
        // "redact this span by deleting it") survives. The fallback
        // only fires when replacement is null/undefined.
        replacement: span.replacement ?? REDACTION_FALLBACK,
      });
    }
    cursor += length;
  }
  return out;
}

function buildPrebakedDraftSpans(draftText) {
  const scriptedDraft = D2L1_DRAFT.spans.map((span) => span.t).join('');
  if (!scriptedDraft.startsWith(draftText)) return [];

  const out = [];
  let cursor = 0;
  for (const span of D2L1_DRAFT.spans) {
    const end = cursor + span.t.length;
    if (span.risk && span.inference && end <= draftText.length) {
      out.push({
        start: cursor,
        end,
        severity: span.risk / 3,
        confidence: span.inference.confidence,
        inferredTrait: span.inference.trait,
        // Scripted rationales can rely on suffix context not yet typed.
        ...(draftText === scriptedDraft ? { justification: span.inference.why } : {}),
        replacement: span.replacement ?? REDACTION_FALLBACK,
      });
    }
    cursor = end;
  }
  return out;
}

export class PrebakedDraftInferenceClient {
  constructor(delegate) {
    this.delegate = delegate;
  }

  analyzeMirror(...args) {
    return this.delegate.analyzeMirror(...args);
  }
  analyzeMeter(...args) {
    return this.delegate.analyzeMeter(...args);
  }
  analyzeMessageFindings(...args) {
    return this.delegate.analyzeMessageFindings(...args);
  }
  generateReply(...args) {
    return this.delegate.generateReply(...args);
  }
  isLocalOnly(method) {
    return method === 'scoreDraft';
  }

  async scoreDraft(request) {
    assertDraftRequest(request);
    const { draft } = request;
    assertOwnAuthorshipScope({ role: 'me', text: draft });
    const spans = buildPrebakedDraftSpans(draft);
    spans.forEach(assertDraftSpan);
    return { spans };
  }
}

export class MockInferenceClient {
  async analyzeMirror({ messages }) {
    assertTranscript(messages);
    await delay(MOCK_LATENCY_MS);
    const mirrorAggregate = buildMirrorAggregate();
    assertMirrorAggregate(mirrorAggregate);
    return { mirrorAggregate };
  }

  async analyzeMeter({ messages }) {
    assertTranscript(messages);
    await delay(MOCK_LATENCY_MS);
    const axisEvidence = Object.fromEntries(
      RISK_AXES.map((axis) => [
        axis,
        adaptAxisEvidence(axis) || {
          axis,
          contributing: [],
          explanation: 'No evidence on file.',
        },
      ])
    );
    const response = {
      meterUpdate: buildMeterUpdate(),
      axisEvidence,
    };
    assertMeterResponse(response);
    return response;
  }

  async analyzeMessageFindings({ messages, targetMessageIds }) {
    assertTranscript(messages);
    assertTargetScope(messages, targetMessageIds);
    await delay(MOCK_LATENCY_MS);
    const byId = new Map(messages.map((message) => [message.id, message]));
    const findingsByMessage = targetMessageIds.map((messageId) => {
      const message = byId.get(messageId);
      const fixtureMsg = CHAT_DATA.messages.find(
        (fixture) =>
          fixture.text === message.text && (fixture.from === 'me' || fixture.from === 'me-draft')
      );
      const messageInferences =
        fixtureMsg?.inferences?.map((inference) =>
          adaptInference(inference, message.text, messageId)
        ) ?? [];
      return { messageId, messageInferences };
    });
    const response = { findingsByMessage };
    assertMessageFindingsResponse(response);
    return response;
  }

  async scoreDraft(request) {
    assertDraftRequest(request);
    const { draft } = request;
    // Drafts are authored by the participant, so their target scope is implicit.
    assertOwnAuthorshipScope({ role: 'me', text: draft });
    await delay(MOCK_LATENCY_MS);

    const spans = buildDraftSpans(draft);
    if (spans === null) {
      if (isDev) console.warn('[mock] miss (draft)', { len: draft.length });
      return { spans: [] };
    }
    if (isDev) console.info('[mock] hit (draft)', { len: draft.length });
    spans.forEach(assertDraftSpan);
    return { spans };
  }

  async generateReply() {
    await delay(MOCK_LATENCY_MS);
    // Mock is test/offline only — a small fixed burst is enough to exercise the
    // multi-message + typing-indicator path. The real reply content comes from
    // the backend. Timing is left to the hook's computeReplyTiming.
    return {
      messages: [{ text: "Oh, that's interesting!" }, { text: 'Where did you say you were from?' }],
    };
  }
}

export class HttpInferenceClient {
  constructor(baseUrl = '', { sessionToken = null } = {}) {
    this.baseUrl = baseUrl;
    this.sessionToken = sessionToken;
  }

  async _post(path, body) {
    const headers = {
      'Content-Type': 'application/json',
      'X-Contract-Version': CONTRACT_VERSION,
    };
    if (this.sessionToken) headers['X-Participant-Session'] = this.sessionToken;
    // This is a browser-side application endpoint, not a server-side request sink.
    // nosemgrep: nodejs_scan.javascript-ssrf-rule-node_ssrf
    const res = await fetch(`${this.baseUrl}${path}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      // Read body for diagnosis. Best-effort: a non-JSON 5xx still gives
      // operators something to grep when this surfaces in logs.
      let detail = '';
      try {
        detail = await res.text();
      } catch {
        /* body unavailable */
      }
      let parsed = null;
      try {
        parsed = detail ? JSON.parse(detail) : null;
      } catch {
        /* non-JSON error body */
      }
      if (res.status === 429 && parsed?.error === 'limit_reached') {
        throw new LimitReachedError(parsed.limit);
      }
      throw new Error(`HTTP ${res.status} ${path}${detail ? ` — ${detail.slice(0, 200)}` : ''}`);
    }
    return res.json();
  }

  async analyzeMirror({ conversationId, messages }) {
    assertTranscript(messages);
    const response = await this._post('/api/infer/mirror', { conversationId, messages });
    assertMirrorAggregate(response?.mirrorAggregate);
    return response;
  }

  async analyzeMeter({ conversationId, messages }) {
    assertTranscript(messages);
    const response = await this._post('/api/infer/meter', { conversationId, messages });
    assertMeterResponse(response);
    return response;
  }

  async analyzeMessageFindings({ conversationId, messages, targetMessageIds }) {
    assertTranscript(messages);
    assertTargetScope(messages, targetMessageIds);
    const response = await this._post('/api/infer/message-findings', {
      conversationId,
      messages,
      targetMessageIds,
    });
    assertMessageFindingsResponse(response);
    return response;
  }

  async scoreDraft(request) {
    assertDraftRequest(request);
    const { draft } = request;
    assertOwnAuthorshipScope({ role: 'me', text: draft });
    const response = await this._post('/api/infer/draft', request);
    if (response === null || typeof response !== 'object' || !Array.isArray(response.spans)) {
      throw new TypeError('[contract] DraftResponse: expected { spans: DraftSpan[] }');
    }
    response.spans.forEach(assertDraftSpan);
    return response;
  }

  async generateReply({ conversationId, history, newMessage }) {
    if (newMessage) assertMessage(newMessage);
    assertHistory(history);
    const response = await this._post('/api/chat', { conversationId, history, newMessage });
    const normalized = normalizeChatResponse(response);
    if (normalized === null) {
      throw new TypeError(
        '[contract] ChatResponse: expected { messages: ChatMessage[] } (or legacy { text: string }) with at least one non-empty message'
      );
    }
    assertChatResponse(normalized);
    return normalized;
  }
}

/**
 * Pick MockInferenceClient (default) or HttpInferenceClient based on
 * the ?mock=0|1 query param. The frontend never branches on which
 * client is returned — it only reads the contract.
 *
 * @param {string} search e.g. window.location.search
 */
export function pickInferenceClient(search = '', options = {}) {
  const { appConfig = defaultAppConfig, ...clientOptions } = options;
  if (resolveClientMode(search, appConfig) === 'http') {
    const client = new HttpInferenceClient('', clientOptions);
    // No arm config requests draftMode: 'prebaked' anymore (D2 word/sentence
    // both hit the live draft endpoint), but the wrapper stays available for
    // scripted/demo use — see PrebakedDraftInferenceClient below.
    return clientOptions.draftMode === 'prebaked'
      ? new PrebakedDraftInferenceClient(client)
      : client;
  }
  const client = new MockInferenceClient();
  return clientOptions.draftMode === 'prebaked' ? new PrebakedDraftInferenceClient(client) : client;
}
